import {
  Box,
  Button,
  DropdownButton,
  DropdownButtonContext,
  DropdownButtonContextProps,
  EmptyState,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  SelectionList,
  Spinner,
  Text,
  TextWithHighlight,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg, SearchQueue } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { appActions } from 'app/app.actions';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { actions as conversationsActions } from 'components/conversation/actions/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { transferToAgentActions } from 'components/shared/transferToAgentModal/actions/transferToAgentModal';
import { transferToQueueActions } from 'components/shared/transferToQueueModal/actions/transferToQueueModal';
import { useCommandHandler } from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { isRestrictedFromQueue, useUserQueues } from 'utils/user/useUserQueues';

import styles from './transferToQueueDropdown.module.scss';

export const TransferToQueueDropdown = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const userQueues = useUserQueues();
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [
    readSearchQueues,
    { results: queues, isFetching, isSuccess, hasNextPage, fetchNextPage },
  ] = useLazyReadSearchInfiniteQuery<'queues'>();

  const initialArg = useMemo(
    (): ReadSearchApiArg<'queues'> => ({
      scope: 'queues',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_archived',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
      },
      sortBy: { field: 'name', order: 'asc' },
      urlParams: {
        include_restricted: true,
      },
    }),
    [],
  );

  useUpdateEffect(() => {
    readSearchQueues(
      { ...initialArg, query: searchTerm || undefined },
      { debounceTime: 300 },
    );
  }, [readSearchQueues, initialArg, searchTerm]);

  const [
    selectedQueue,
    csid,
    transferToQueueRequests,
    metaConversation,
    hasActivePhoneCall,
  ] = useAppSelector(
    (state) => [
      state.conversation.metaConversation.conversation.queue,
      state.conversation.metaConversation.conversation.id,
      state.transferToQueue.requests,
      state.conversation.metaConversation,
      state.telephony.currentCall !== null,
    ],
    shallowEqual,
  );

  const initialChannel = metaConversation?.conversation.initial_channel;
  const isPhoneConversation = initialChannel === 'pstnphone';
  const isLivePhoneConversation =
    isPhoneConversation &&
    !metaConversation?.conversation.is_closed &&
    hasActivePhoneCall;

  const { warmTransferToQueue: isWarmTransferToQueueEnabled } = useFeatureFlag([
    'warm-transfer-to-queue',
  ]);

  // "Call agent first" warm-transfers via a live phone call, so it must only
  // be offered on actually-live phone conversations - not closed ones, and not
  // open-but-no-active-call ones.
  const showCallAgentFirstOption =
    isWarmTransferToQueueEnabled && isLivePhoneConversation;

  const selectedQueueItem = selectedQueue as SearchQueue;

  const queueWithoutSelectedQueue = useMemo(() => {
    if (selectedQueue && !searchTerm) {
      return queues.filter((queue) => queue.id !== selectedQueue.id);
    }
    return queues;
  }, [queues, searchTerm, selectedQueue]);

  const isTransferringToQueue = !!transferToQueueRequests.find(
    (request) => request.csid === csid,
  );

  const handleQueueOnSelect = useCallback(
    async (selectQueue: SearchQueue) => {
      ampli.conversationHeaderTransferToQueueItemSelected({
        queue: selectQueue.name,
      });

      setSearchTerm('');
      setIsDropdownOpen(false);

      const { isConfirmed, isSecondaryClicked } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              id: 'transferToQueue',
              title: 'Transfer conversation',
              description: `Are you sure you want to transfer this conversation to "${selectQueue.name}"?`,
              confirm: {
                variant: 'primary',
              },
              ...(showCallAgentFirstOption && {
                secondary: {
                  label: 'Call agent first',
                },
              }),
            },
          },
        }),
      );

      if (isSecondaryClicked && metaConversation) {
        dispatch(
          transferToAgentActions.transferToAgentModalOpen({
            metaConversation,
            queueId: selectQueue.id,
          }),
        );
        return;
      }

      if (!isConfirmed) {
        return;
      }

      if (selectQueue.is_restricted) {
        const { isConfirmed } = await dispatch(
          appActions.showModal({
            modal: {
              type: 'app:confirm',
              arg: {
                id: 'transferToRestrictedQueue',
                title: 'Transfer conversation to a restricted queue',
                description: `Transferring this conversation to "${selectQueue.name}" may make it inaccessible to team members who are not part of that queue. Are you sure you want to proceed?`,
                confirm: {
                  variant: 'primary',
                },
              },
            },
          }),
        );

        if (!isConfirmed) {
          return;
        }
      }

      const transferToQueue = async (csid: number, toQueue: SearchQueue) => {
        const { transferToQueueModalRequest } = transferToQueueActions;
        try {
          await bindActionToPromise(
            dispatch,
            transferToQueueModalRequest,
          )({
            csid,
            requestToQueue: toQueue,
          });

          ampli.conversationTransferred({
            conversationId: csid,
            option: 'queue',
          });
        } catch (error) {
          console.warn(error);
          dispatch(
            notificationActions.notificationAdd({
              title: 'Could not transfer the conversation',
              text: `An issue occurred while trying transfer conversation ${csid}.`,
              status: 'danger',
              icon: 'exclamation',
            }),
          );
        }
      };

      const isRestricted = isRestrictedFromQueue(selectQueue, userQueues);

      if (csid) {
        transferToQueue(csid, selectQueue);

        if (isRestricted) {
          navigate({ path: '/conversations', params: {} });
          dispatch(conversationsActions.conversationsRemove(csid));
        }
      }
    },
    [
      userQueues,
      csid,
      dispatch,
      navigate,
      showCallAgentFirstOption,
      metaConversation,
    ],
  );

  const handleOnToggle = useCallback(
    (isOpen: boolean) => {
      setIsDropdownOpen(isOpen);
      if (isOpen) {
        inputRef?.current?.focus();
        ampli.conversationHeaderTransferToQueueMenuOpened();
        readSearchQueues(
          { ...initialArg, query: searchTerm || undefined },
          { debounceTime: 0 },
        );
      } else {
        setSearchTerm('');
      }
    },
    [readSearchQueues, initialArg, searchTerm],
  );

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:transferToQueue">
      Transfer to queue
    </CommandKeybindingsTooltipContent>
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <DropdownButton
      noStyle
      data-testid="conversation-transfer-to-queue-dropdown"
      dropdown={
        <div className={styles.dropdown}>
          <Box padding="300">
            <InputText
              ref={inputRef}
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value !== undefined ? event.target.value : '',
                )
              }
              placeholder="Search..."
              suffix={{
                type: 'custom',
                value: (
                  <FlexBox alignItems="center" padding="0 100">
                    <IconButton
                      variant="tertiary"
                      icon={searchTerm ? 'close' : 'search'}
                      onClick={searchTerm ? handleClearSearch : undefined}
                    />
                  </FlexBox>
                ),
                noWrap: true,
              }}
            />
          </Box>
          <SelectionList
            className={styles.list}
            selectedValues={selectedQueueItem ? [selectedQueueItem] : undefined}
            onChange={async (selectionList) => {
              handleQueueOnSelect(selectionList.selectedValues[0]);
            }}
            defaultActiveItemIndex={0}
            isActive={isDropdownOpen}
          >
            {selectedQueueItem && !searchTerm && (
              <SelectionList.Item
                key={selectedQueueItem.id}
                value={selectedQueueItem}
                className={styles.listItem}
              >
                <Text.Paragraph>
                  <TextWithHighlight
                    text={selectedQueueItem.name}
                    highlight={searchTerm}
                  />
                </Text.Paragraph>
              </SelectionList.Item>
            )}
            {queueWithoutSelectedQueue.map((queue) => (
              <SelectionList.Item
                key={queue.id}
                value={queue}
                className={styles.listItem}
              >
                <FlexBox
                  justifyContent="flex-start"
                  alignItems="center"
                  gap="200"
                >
                  <Text.Paragraph>
                    <TextWithHighlight
                      text={queue.name}
                      highlight={searchTerm}
                    />
                  </Text.Paragraph>
                  {queue.is_restricted && (
                    <Icon name="lock" size={16} style={{ flexShrink: 0 }} />
                  )}
                </FlexBox>
              </SelectionList.Item>
            ))}

            {isFetching && (
              <FlexBox as="li" padding="300" justifyContent="center">
                <Spinner size={20} />
              </FlexBox>
            )}

            {isSuccess && !queues.length && (
              <FlexBox as="li" padding="0 300 300" justifyContent="center">
                <EmptyState
                  title="No queues found"
                  description="Try changing your search criteria."
                />
              </FlexBox>
            )}

            {!isFetching && hasNextPage && <Waypoint onEnter={fetchNextPage} />}
          </SelectionList>
        </div>
      }
      onToggle={handleOnToggle}
      buttonProps={{
        variant: 'tertiary',
        icon: 'flow',
        disabled: isTransferringToQueue,
      }}
      show={isDropdownOpen}
    >
      <Tooltip
        content={tooltipContent}
        disabled={isTransferringToQueue || isDropdownOpen}
      >
        <Button
          icon="flow"
          slotEnd={<Icon name="angle-down" />}
          variant="tertiary"
          isLoading={!!selectedQueue && selectedQueue.name === selectedQueue.id}
        >
          {isTransferringToQueue
            ? 'Transfer in progress...'
            : selectedQueue?.name || 'Not in queue'}
        </Button>
      </Tooltip>
      <DropdownButtonContext.Consumer>
        {(props) => <TransferToQueueCommandHandler {...props} />}
      </DropdownButtonContext.Consumer>
    </DropdownButton>
  );
};

const TransferToQueueCommandHandler = ({
  dropdownOpen,
  openDropdown,
  closeDropdown,
}: DropdownButtonContextProps): null => {
  useCommandHandler({
    name: 'conversation:transferToQueue',
    actionFn: () => {
      // We have to do this as toggleDropdown doesn't seem to work in this case.
      if (dropdownOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
    },
  });

  return null;
};
